/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import { typographyVars } from '@oracle/oraclejet-internal-theme-contract/themeContract.css';
import { GetTextDimensionsType } from '#hooks/PRIVATE_useTextDimensions';
import { xUnits } from '#UNSAFE_Theme';
import {
  getDefaultReferenceLineLabelFontSize,
  hasLabelledReferenceLine
} from '#utils/PRIVATE_meterUtils/meterUtils';
import { ColorProps } from '#utils/UNSAFE_interpolations/colors';
import { MeterSize, ReferenceLine } from '#utils/UNSAFE_meterUtils';
import { getStartAngleForValue } from './circleMeterUtils';
import { getInnerRadius, getTrackSize, MeterInfo } from './layoutUtils';

const LABEL_GAP = 4;

import { CircularRefLine } from './meterCircle.types';

/**
 * Returns the calculated length of reference line for given size and inner radius of meter circle.
 * The length of reference line runs from the inner radius to slight outside the outer radius.
 * The length outside the outer radius depends on size of the meter circle.
 * @param size The size of the meter circle.
 * @param innerRadius The inner radius of the meter circle.
 * @returns The calculated CSS var for the length of reference line.
 */
export function getReferenceLineLength(
  size: MeterSize,
  innerRadius?: number,
  isMin?: boolean,
  diameter?: number
): string {
  return `${getTrackSize(size, innerRadius, diameter)} +  ${xUnits(
    size === 'fit' && !isMin ? 4 : 2
  )}`;
}

/**
 * Returns the calculated props for CircularReferenceLine.
 * @param meterInfo MeterInfo object containing info about the meter circle.
 * @param refLine The CircularRefLine object
 * @param innerRadius The innerRadius of the meter circle.
 * @returns An bject with calculated props for CircularReferenceLine.
 */
export function getReferenceLinesProps(
  meterInfo: MeterInfo,
  refLine: CircularRefLine,
  innerRadius?: number,
  isMin?: boolean,
  diameter?: number
): {
  radius: string;
  length: string;
  angle: number;
  color?: ColorProps['color'];
} {
  const { min, max, startAngle, angleExtent, size, isRtl } = meterInfo;
  return {
    radius: getInnerRadius(size, innerRadius, diameter),
    length: getReferenceLineLength(size, innerRadius, isMin, diameter),
    angle: getStartAngleForValue(refLine.value, min, max, startAngle, angleExtent, isRtl),
    color: refLine.color
  };
}

/**
 * Returns the width and height of the fit meter circle when reference line
 * and reference line label is present.
 * @returns
 */
export function getWidthAndHeightWithReferenceLine(
  _width: number,
  _height: number,
  hasReferenceLine: boolean,
  breakPoint: 'min' | 'xs' | 'sm' | 'md',
  hasReferenceLineLabel: boolean,
  maxRefLabelDims: { width: number; height: number }
) {
  if (!hasReferenceLine) {
    return { width: _width, height: _height, overHang: 0 };
  }
  const overHang = getReferenceLineOverHang(breakPoint);
  const labelOffset = getReferenceLineLabelGap(breakPoint);
  return {
    width: _width * (hasReferenceLineLabel ? 0.6 : 1) - 2 * overHang,
    height: _height - 2 * overHang - 2 * (maxRefLabelDims.height + labelOffset),
    overHang
  };
}

/**
 * Returns the maximum dimensions of reference lines labels.
 */
export function getMaxReferenceLineLabelDims(
  breakPoint: 'min' | 'xs' | 'sm' | 'md',
  refLines?: ReferenceLine[],
  getTextDimensions?: GetTextDimensionsType
) {
  if (!hasLabelledReferenceLine(refLines) || !getTextDimensions || !refLines) {
    return { width: 0, height: 0 };
  }
  return refLines
    .map((line) => {
      const { label, labelStyle } = line;
      const dims = label
        ? getTextDimensions(label, {
            fontSize: getDefaultReferenceLineLabelFontSize(breakPoint),
            lineHeight: typographyVars.body.md.lineHeight,
            ...labelStyle
          })
        : { width: 0, height: 0 };
      return dims;
    })
    .reduce(
      (acc, cur) => {
        return {
          width: Math.max(cur.width, acc.width),
          height: Math.max(cur.height, acc.height)
        };
      },
      { width: 0, height: 0 }
    );
}

/**
 * Returns the gap for the reference line label.
 */
export function getReferenceLineLabelGap(breakPoint: 'min' | 'xs' | 'sm' | 'md', angle?: number) {
  const isMin = breakPoint === 'min' || breakPoint === 'xs';

  // vertical gap
  if (angle != null && ((angle < 108 && angle > 72) || (angle < 288 && angle > 252))) {
    return isMin ? 0 : LABEL_GAP;
  }

  // horizontal gap

  if (angle != null && (!(angle < 108 && angle > 72) || (angle < 288 && angle > 252))) {
    return 1.5 * LABEL_GAP;
  }

  return isMin ? LABEL_GAP / 2 : LABEL_GAP;
}

/**
 * Returns the length of reference line outside the diameter of the meter circle.
 */
export function getReferenceLineOverHang(breakPoint: 'min' | 'xs' | 'sm' | 'md') {
  // these numbers are from the meter circle spec.
  return breakPoint === 'min' || breakPoint === 'xs' ? LABEL_GAP * 2 : LABEL_GAP * 4;
}
